// IndicativeQuotesMobileComponent.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const IndicativeQuotesMobileComponent = ({ data, color }) => {
  const {
    title,
    description,
    footerText,
    quotes,
    footerLink,
    footerLinkDescription,
  } = data;
  const isGrey = color === 'grey';

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        maxWidth: '100vw',
        backgroundColor: color === 'grey' ? '#E3E6E8' : '#162F3B',
        borderRadius: '34px',
        padding: '32px 16px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Title */}
      <ScrollFadeWrapper variant="text">
        <Typography
          component="h3"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '2rem',
            lineHeight: '2.25rem',
            letterSpacing: '-0.03em',
            color: isGrey ? '#162F3B' : '#FFFFFF',
            textAlign: 'center',
            mb: '16px',
          }}
        >
          {title}
        </Typography>
      </ScrollFadeWrapper>

      {/* Description */}
      <ScrollFadeWrapper variant="text" delay={0.2}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1.25rem',
            lineHeight: '1.5rem',
            letterSpacing: '-0.03em',
            color: color === 'grey' ? '#162F3B' : '#FFFFFF',
            textAlign: 'center',
            mb: '40px',
            px: '16px',
            alignSelf: 'stretch',
            boxSizing: 'border-box',
            whiteSpace: 'pre-line',
          }}
        >
          {description?.replace(
            '{CURRENT_MONTH_YEAR}',
            new Date().toLocaleString('en-GB', {
              month: 'long',
              year: 'numeric',
            })
          )}
        </Typography>
      </ScrollFadeWrapper>

      {/* Quote Cards */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          alignItems: 'center',
        }}
      >
        {quotes.map((quote, index) => (
          <ScrollFadeWrapper
            key={index}
            delay={0.4 + index * 0.1}
            sx={{ alignSelf: 'stretch' }}
          >
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '48px',
                padding: '24px 16px',
                boxShadow:
                  '0px 1px 2px rgba(162, 172, 177, 0.2), 0px 12px 24px -4px rgba(22, 47, 59, 0.12)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                alignSelf: 'stretch',
                boxSizing: 'border-box',
              }}
            >
              <Image
                width={222}
                height={168}
                src={quote.image}
                alt={quote.imgAltTag || `Quote ${index + 1}`}
                title={quote.imageAltTitle}
                sx={{
                  width: '222px',
                  height: '168px',
                  mb: '20px',
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.02em',
                  color: '#162F3B',
                  whiteSpace: 'pre-line',
                }}
              >
                {quote.text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
                  if (/^\*\*[^*]+\*\*$/.test(part)) {
                    return (
                      <strong key={i} style={{ fontWeight: 700 }}>
                        {part.slice(2, -2)}
                      </strong>
                    );
                  }
                  return part;
                })}
              </Typography>
            </Box>
          </ScrollFadeWrapper>
        ))}
      </Box>

      {/* Footer */}
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: '1.5rem',
          letterSpacing: '-0.02em',
          color: color === 'grey' ? '#162F3B' : '#FFFFFF',
          textAlign: 'center',
          mt: '64px',
          px: '16px',
          boxSizing: 'border-box',
          alignSelf: 'stretch',
          whiteSpace: 'pre-line',
        }}
      >
        {footerText?.replace(
          '{CURRENT_MONTH_YEAR}',
          new Date().toLocaleString('en-GB', {
            month: 'long',
            year: 'numeric',
          })
        )}
      </Typography>
      {/* Footer Link */}
      <LinkWithIconComponent
        href={footerLink || undefined}
        iconSrc="https://assets.askhippo.co.za/Icons/common/Union.svg"
        iconSize="0.875rem"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '1rem',
          fontWeight: 700,
          lineHeight: '1.5rem',
          letterSpacing: '-0.02em',
          color: color === 'grey' ? '#162F3B' : '#FFFFFF',
          textAlign: 'center',
          mt: '10px',
          px: '16px',
          boxSizing: 'border-box',
          alignSelf: 'stretch',
          whiteSpace: 'pre-line',
        }}
      >
        {footerLinkDescription}
      </LinkWithIconComponent>
    </ScrollFadeSection>
  );
};

export default IndicativeQuotesMobileComponent;
